import { VipGroupName } from '../modules/vip/helpers';
import { isBetweenUTC } from './dateTime';
import {
  COMMON_VIP_GROUPS,
  groupMember,
  someGroupMember,
  USER_GROUP,
  vipGroups,
} from './user';
import { User, UserStatus } from '@/api/auth';
import { TopSpotGame } from '@/api/cms/topStopGame';
import { useCommonStore } from '@/store';

const COMPOINTS_KOEF = 100;
export const SUPER_SPOT_BREAKPOINT_VALUE = 6; // ex. GOLD_TSG_6, GOLD_TSG_7

const regexTSG = /^TSG_[0-9]+$/;
const vipNameValues = Object.values(VipGroupName).join('|');
const regexTierTSG = new RegExp(`^(${vipNameValues})_TSG_[0-9]+$`);
const getUserVipGroup = (statuses: UserStatus[]) =>
  statuses.find(({ id }) => vipGroups.includes(id as USER_GROUP))?.id;

export interface TopSpotStaticData {
  dashboardImg: string;
  infoImg: string;
  vipTypeName: string;
  prizeText: string;
}

export const topSpotGameStaticDataMap: Record<string, TopSpotStaticData> = {
  [USER_GROUP.SILVER]: {
    dashboardImg: '/topSpotGame/pVipCredit.png',
    infoImg: '/topSpotGame/pVipCredit.png',
    vipTypeName: '',
    prizeText: '',
  },
  [USER_GROUP.GOLD]: {
    dashboardImg: '/topSpotGame/goldCredit.webp',
    infoImg: '/topSpotGame/top-spot-info-golden.webp',
    vipTypeName: 'Gold',
    prizeText: 'vipNamePrize.gold',
  },
  [USER_GROUP.PLATINUM]: {
    dashboardImg: '/topSpotGame/platinumCredit.webp',
    infoImg: '/topSpotGame/top-spot-info-platinum.webp',
    vipTypeName: 'Platinum',
    prizeText: 'vipNamePrize.platinum',
  },
  [USER_GROUP.DIAMOND]: {
    dashboardImg: '/topSpotGame/diamondCredit.webp',
    infoImg: '/topSpotGame/top-spot-info-diamond.webp',
    vipTypeName: 'Diamond',
    prizeText: 'vipNamePrize.diamond',
  },
  [USER_GROUP.SUPER_SPOT]: {
    dashboardImg: '/topSpotGame/superCredit.webp',
    infoImg: '/topSpotGame/top-spot-info-super.webp',
    vipTypeName: 'Super',
    prizeText: 'vipNamePrize.super',
  },
};

const getTopSpotGameStaticData = (
  statuses: UserStatus[],
  isPassedSuperSpotBreakpoint: boolean
) => {
  const vipGroup = isPassedSuperSpotBreakpoint
    ? USER_GROUP.SUPER_SPOT
    : getUserVipGroup(statuses);

  if (vipGroup) return topSpotGameStaticDataMap[vipGroup];
};

export const getUserCompointsByTSGGroup = (user: User) => {
  const groupNumber = getTSGGroupNumber(user, regexTSG);

  if (!groupNumber) return 0;

  return groupNumber * COMPOINTS_KOEF;
};

export const getUserTarget = (
  statuses: UserStatus[],
  topSpot: TopSpotGame,
  isPassedSuperSpotBreakpoint: boolean
) => {
  const vipGroup = getUserVipGroup(statuses);

  if (!vipGroup) return;

  const targetByVipMap: Record<string, number> = {
    [USER_GROUP.GOLD]: topSpot.vip1Goal,
    [USER_GROUP.PLATINUM]: topSpot.vip2Goal,
    [USER_GROUP.DIAMOND]: topSpot.vip3Goal,
  };

  const targetBySuperVipMap: Record<string, number> = {
    [USER_GROUP.GOLD]: topSpot.superVip1Goal,
    [USER_GROUP.PLATINUM]: topSpot.superVip2Goal,
    [USER_GROUP.DIAMOND]: topSpot.superVip3Goal,
  };

  return isPassedSuperSpotBreakpoint
    ? targetBySuperVipMap[vipGroup]
    : targetByVipMap[vipGroup];
};

const getTSGGroupNumber = (user: User, groupRegex: RegExp) => {
  const userTSGGroup = user.statuses?.findLast(({ id }) =>
    id.match(groupRegex)
  );

  if (!userTSGGroup) return;

  const groupNumber = Number(userTSGGroup.id.split('_').slice(-1)[0]);

  if (isNaN(groupNumber)) return;

  return groupNumber;
};

export const calculateUserProgressByGroup = (user: User) => {
  const groupNumber = getTSGGroupNumber(user, regexTierTSG);

  if (!groupNumber) {
    if (groupMember(USER_GROUP.TSG_START)(user)) {
      return 1;
    }
    return 0;
  }

  const percentage = (groupNumber / 12) * 100;

  return percentage;
};

export const calculateUserByCP = (compoints: number, target: number) =>
  Math.floor((compoints / target) * 100);

export const checkIsPassedSuperSpotBreakpoint = (user: User) => {
  const groupNumber = getTSGGroupNumber(user, regexTierTSG);

  if (!groupNumber) return false;

  return groupNumber >= SUPER_SPOT_BREAKPOINT_VALUE;
};

export enum TopSpotGameStatus {
  PVIP = 'pVIP',
  ACTIVE = 'ACTIVE',
  COMPLETE = 'COMPLETE',
}

export const getTopSpotGameStatus = (user: User) => {
  if (groupMember(USER_GROUP.SILVER)(user)) return TopSpotGameStatus.PVIP;
  if (groupMember(USER_GROUP.TSG_COMPLETE)(user))
    return TopSpotGameStatus.COMPLETE;
  if (someGroupMember(COMMON_VIP_GROUPS)(user)) return TopSpotGameStatus.ACTIVE;
};

export const updateTSGData = (topSpot: TopSpotGame, user: User) => {
  const { statuses } = user;

  if (!statuses) return;

  const topSpotGameStatus = getTopSpotGameStatus(user);
  const isSuperVip = groupMember(USER_GROUP.SUPER_SPOT)(user);
  const isPassedSuperSpotBreakpoint = checkIsPassedSuperSpotBreakpoint(user);

  const topSpotGameStaticData = getTopSpotGameStaticData(
    statuses,
    isPassedSuperSpotBreakpoint
  );

  if (!topSpotGameStatus || topSpotGameStatus === TopSpotGameStatus.PVIP)
    return useCommonStore.setState({
      topSpotGameStatus,
      topSpotGameStaticData,
    });

  const userTarget = getUserTarget(
    statuses,
    topSpot,
    isPassedSuperSpotBreakpoint
  );

  if (!userTarget) return;

  const userCompointsByTSGGroup = getUserCompointsByTSGGroup(user);
  const userTSGProgressPercentByGroup = calculateUserProgressByGroup(user);

  const userTSGProgressPercentByCP =
    topSpotGameStatus === TopSpotGameStatus.COMPLETE
      ? 100
      : calculateUserByCP(userCompointsByTSGGroup, userTarget);

  useCommonStore.setState({
    isSuperVip,
    userTSGProgressPercentByGroup,
    userTSGProgressPercentByCP,
    userTarget,
    topSpotGameStatus,
    topSpotGameStaticData,
    isPassedSuperSpotBreakpoint,
    isTopSpotGameAvailable: isBetweenUTC(topSpot.startDate, topSpot.endDate),
  });
};
